/**
 * Admin Visa Outcomes Tab
 * Counsellor team logs every real visa decision (approved / refused / withdrawn)
 * here. The predictor reads these monthly to adjust per-country base scores.
 *
 * Backend:
 *   GET    /api/visa-probability/outcomes
 *   POST   /api/visa-probability/outcomes
 *   DELETE /api/visa-probability/outcomes/{id}
 *   POST   /api/visa-probability/recalibrate
 */
import { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, RefreshCw, Loader2, TrendingUp, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const COUNTRIES = [
  'United Kingdom', 'Germany', 'France', 'New Zealand', 'Australia',
  'Canada', 'Ireland', 'Netherlands', 'UAE / Dubai', 'United States',
];

const OUTCOME_BADGE = {
  approved: 'bg-emerald-100 text-emerald-800',
  refused: 'bg-rose-100 text-rose-800',
  withdrawn: 'bg-gray-100 text-gray-700',
};

export default function VisaOutcomesTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [recalibrating, setRecalibrating] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({
    student_name: '', nationality: '', country: '', outcome: 'approved',
    decision_date: '', refusal_reason: '', notes: '',
  });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/visa-probability/outcomes');
      setRows(data.outcomes || []);
      setSummary(data.summary || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load outcomes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const save = async () => {
    if (!form.student_name || !form.country) {
      toast.error('Student name and country are required');
      return;
    }
    setAdding(true);
    try {
      await api.post('/visa-probability/outcomes', form);
      toast.success('Outcome recorded');
      setForm({ student_name: '', nationality: '', country: '', outcome: 'approved', decision_date: '', refusal_reason: '', notes: '' });
      setShowDialog(false);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this outcome record?')) return;
    try {
      await api.delete(`/visa-probability/outcomes/${id}`);
      toast.success('Deleted');
      fetchRows();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const recalibrate = async () => {
    setRecalibrating(true);
    try {
      const { data } = await api.post('/visa-probability/recalibrate');
      const adjusted = Object.keys(data.deltas || {}).length;
      toast.success(`Recalibrated · ${adjusted} country deltas updated`);
    } catch (e) {
      toast.error('Recalibration failed');
    } finally {
      setRecalibrating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="visa-outcomes-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-teal-500" />
            Visa Outcomes
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Record every real visa decision. The AI predictor uses this data to calibrate country base scores.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={recalibrate} disabled={recalibrating} data-testid="recalibrate-btn">
            {recalibrating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Recalibrate
          </Button>
          <Button onClick={() => setShowDialog(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-outcome-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Outcome
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {COUNTRIES.map((c) => {
          const s = summary[c] || { approved: 0, refused: 0, total: 0 };
          const rate = s.total > 0 ? Math.round((s.approved / s.total) * 100) : null;
          return (
            <div key={c} className="border border-gray-200 dark:border-slate-700 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{c}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-xl font-bold text-emerald-600">{rate != null ? `${rate}%` : '—'}</span>
                <span className="text-xs text-gray-500">{s.total} records</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">✓ {s.approved} · ✗ {s.refused}</p>
            </div>
          );
        })}
      </div>

      {/* Rows */}
      <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-200">
            <tr>
              <th className="text-left px-3 py-2">Student</th>
              <th className="text-left px-3 py-2">Nationality</th>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-left px-3 py-2">Outcome</th>
              <th className="text-left px-3 py-2">Decision date</th>
              <th className="text-left px-3 py-2">Recorded by</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-6 text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">
                <TrendingUp className="inline w-5 h-5 mr-1 text-gray-400" />
                No outcomes recorded yet. Start logging real visa decisions to improve AI accuracy.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 dark:border-slate-700">
                <td className="px-3 py-2 font-medium">{r.student_name}</td>
                <td className="px-3 py-2 text-gray-600">{r.nationality || '—'}</td>
                <td className="px-3 py-2">{r.country}</td>
                <td className="px-3 py-2"><Badge className={`${OUTCOME_BADGE[r.outcome]} capitalize`}>{r.outcome}</Badge></td>
                <td className="px-3 py-2 text-gray-500 text-xs">{r.decision_date || '—'}</td>
                <td className="px-3 py-2 text-gray-500 text-xs">{r.recorded_by_name}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`delete-outcome-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="add-outcome-dialog">
          <DialogHeader>
            <DialogTitle>Record Visa Outcome</DialogTitle>
            <DialogDescription>
              Record a real visa decision. The AI predictor uses this data to calibrate country base scores monthly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Student name *</Label><Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} data-testid="outcome-student-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nationality</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} placeholder="e.g. Pakistan" /></div>
              <div>
                <Label>Country *</Label>
                <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger data-testid="outcome-country"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Outcome *</Label>
                <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                  <SelectTrigger data-testid="outcome-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="refused">Refused</SelectItem>
                    <SelectItem value="withdrawn">Withdrawn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Decision date</Label><Input type="date" value={form.decision_date} onChange={(e) => setForm({ ...form, decision_date: e.target.value })} /></div>
            </div>
            {form.outcome === 'refused' && (
              <div><Label>Refusal reason</Label><Input value={form.refusal_reason} onChange={(e) => setForm({ ...form, refusal_reason: e.target.value })} placeholder="e.g. Insufficient funds, weak ties to home country…" /></div>
            )}
            <div><Label>Internal notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={save} disabled={adding} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-outcome-btn">
                {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Record
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
